/*
 * Copyright © 2024 Himitsu Lab Limited. All Rights Reserved.
 */

import * as React from 'react'
import { LiveKitConfig } from './CustomLivekitRoom'
import { UserLayoutPosition } from '../liveKitHelpers'
import { TrackReferenceOrPlaceholder } from '@livekit/components-react'

type CustomRoomContextTypes = {
  title: string
  isAudioOnly: boolean
  isMinimized: boolean
  isLiveShopping: boolean

  isUnmuteAllowed: boolean

  unpinnedLayoutPositions: UserLayoutPosition[]
  stagedLayoutPositions: UserLayoutPosition[]
  otherLayoutPositions: UserLayoutPosition[]

  stagedParticipants: string[]

  isHostOrCohostAvailable: boolean
  gridDimensions: { width: number; height: number }
  shouldLeaveMeeting: boolean
  handRaisedParticipants: string[]
  isParticipantHandRaised: boolean
  isHandRaised: boolean
  isHostEnded: boolean

  viewType: 'grid' | 'hostHighlighted'

  allTracks: TrackReferenceOrPlaceholder[]
  allTracksSids: string[]

  isLocalHost: boolean
  dualSelection: boolean

  bidAmount: any
  lastSentBid: number | null

  profileImg: string
  acceptedUserSid: string
  isBidClicked: boolean
  finalBid: number
  onUpdateBid: number
  productId: string

  initialBid: number
  receivedProductId: string
  likedParticipants: string[]
  showBidError: boolean

  recording: boolean
  audioRecording: boolean
  endClicked: boolean
  endLiveShopping: boolean
  joinedParticipants: string[]

  hasShownRecordingToaster: boolean
  hasShownAudioRecordingToaster: boolean
  shareClicked: boolean
  saveClicked: boolean
  noTracks: boolean
  auctionDuration: number

  viewProfileOpen: boolean

  acceptedBidAmount: number | null
  acceptedName: string | undefined
  localAudioRecording: boolean

  setIsMinimized: (isMinimized: boolean) => void
  setStagedParticipants: (stagedParticipant: string[]) => void
  setUnpinnedLayoutPositions: (unpinnedLayoutPositions: UserLayoutPosition[]) => void
  setIsUnmuteAllowed: (isUnmuteAllowed: boolean) => void
  setStagedLayoutPositions: (stagedLayoutPositions: UserLayoutPosition[]) => void
  setOtherLayoutPositions: (otherLayoutPositions: UserLayoutPosition[]) => void
  onMessage?: (message: string, type: 'error' | 'info' | 'success') => void
  setIsHostOrCohostAvailable: (isHostOrCohostAvailable: boolean) => void
  setGridDimensions: (dimensions: { width: number; height: number }) => void
  setShouldLeaveMeeting: (shouldLeaveMeeting: boolean) => void
  setHandRaisedParticipants: React.Dispatch<React.SetStateAction<string[]>>
  setIsParticipantHandRaised: (isParticipantHandRaised: boolean) => void
  setIsHandRaised: (isHandRaised: boolean) => void
  setIsHostEnded: (isHostEnded: boolean) => void
  setViewType: (viewType: 'grid' | 'hostHighlighted') => void
  setAllTracks: (allTracks: TrackReferenceOrPlaceholder[]) => void
  setAllTracksSids: React.Dispatch<React.SetStateAction<string[]>>
  setIsLocalHost: (isLocalHost: boolean) => void
  setDualSelection: (dualSelection: boolean) => void
  setBidAmount: (bidAmount: any) => void
  setLastSentBid: (lastSentBid: number) => void
  setAcceptedUserSid: (acceptedUserSid: string) => void
  setIsBidClicked: (isBidClicked: boolean) => void
  setFinalBid: (finalBid: number) => void
  setOnUpdateBid: (onUpdateBid: number) => void
  setProductId: (productId: string) => void
  setInitialBid: (initialBid: number) => void
  setReceivedProductId: (receivedProductId: string) => void
  setLikedParticipants: React.Dispatch<React.SetStateAction<string[]>>
  setShowBidError: (showBidError: boolean) => void
  setRecording: (recording: boolean) => void
  setAudioRecording: (audioRecording: boolean) => void
  setEndClicked: (endClicked: boolean) => void
  setEndLiveShopping: (endLiveShopping: boolean) => void
  setJoinedParticipants: React.Dispatch<React.SetStateAction<string[]>>
  setHasShownRecordingToaster: (hasShownRecordingToaster: boolean) => void
  setHasShownAudioRecordingToaster: (hasShownAudioRecordingToaster: boolean) => void
  setShareClicked: (onShareClicked: boolean) => void
  setSaveClicked: (onSaveClicked: boolean) => void
  setNoTracks: (noTracks: boolean) => void
  setAuctionDuration: (auctionDuration: number) => void
  setViewProfileOpen: (viewProfileOpen: boolean) => void
  setAcceptedBidAmount: (acceptedBidAmount: number | null) => void
  setAcceptedName: (acceptedName: string | undefined) => void
  setLocalAudioRecording: (localAudioRecording: boolean) => void
}

/** @public */
export const CustomRoomContext = React.createContext<(CustomRoomContextTypes & LiveKitConfig) | undefined>(undefined)

/**
 * Ensures that a room is provided via context.
 * If no room is provided, an error is thrown.
 *
 * @return {CustomRoomContextTypes & LiveKitConfig} The room context if it exists.
 */

export function useCustomRoomContext() {
  const ctx = React.useContext(CustomRoomContext)
  if (!ctx) {
    throw Error('tried to access room context outside of livekit room component')
  }
  return ctx
}

/**
 * Returns the room context if it exists, otherwise undefined.
 *
 * @return {CustomRoomContextTypes & LiveKitConfig | undefined} The room context if it exists, otherwise undefined.
 */

export function useMaybeCustomRoomContext() {
  return React.useContext(CustomRoomContext)
}

// /**
//  * Ensures that a room is provided, either via context or explicitly as a parameter.
//  * If no room is provided, an error is thrown.
//  * @public
//  */
// export function useEnsureRoom(room?: Room) {
//   const context = useMaybeCustomRoomContext();
//   const r = room ?? context;
//   if (!r) {
//     throw new Error(
//       'No room provided, make sure you are inside a Room context or pass the room explicitly',
//     );
//   }
//   return r;
// }
